'use client'

import { type FC } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'
import SvgPause from '@/icons/solid/Pause'
import SvgPlay from '@/icons/solid/Play'
import { tw } from '@/lib/utils'

import { AudioProgressRing } from '../audio-progress-ring'
import { useAudioPlayer } from '../use-audio-player'

interface AudioCardProps {
  url: string
  title?: string
  group?: string
  className?: string
}

export const AudioCard: FC<AudioCardProps> = ({ url, title, group, className }) => {
  const { t } = useTranslation()
  const { audioRef, status, toggle, isMounted, formattedCurrentTime, formattedDuration, audioProps } = useAudioPlayer({
    url,
    group,
  })

  if (!isMounted) return null

  return (
    <div
      className={tw(
        'boxShadow-sm border-navy-10 flex w-full max-w-[640px] items-start justify-between gap-x-[20px] gap-y-[16px] rounded-[20px] border',
        'text-navy my-3 cursor-pointer bg-white px-[16px] py-[20px] shadow-sm lg:flex-row lg:items-center lg:px-[24px] lg:py-[24px]',
        className
      )}
      onClick={toggle}
    >
      <div className="ml-[3px] w-full">
        {title && (
          <div className="text-navy text-[16px] leading-[28px] font-medium tracking-[-0.6px] lg:text-[20px]">
            {title}
          </div>
        )}
        <div className="mt-[4px] text-[14px] leading-[19.6px] tracking-[-0.42px] text-[#545454]">
          {formattedCurrentTime}
          {status.duration > 0 ? ` / ${formattedDuration}` : ''}
        </div>
      </div>
      <div className="flex items-center">
        <button
          type="button"
          data-tracking="ignore"
          aria-label={`${status.isPlaying ? t('audio.pause', 'Pause audio') : t('audio.play', 'Play audio')}${title ? ` – ${title}` : ''}`}
          aria-pressed={status.isPlaying}
          className="focus-visible:outline-lapis relative size-[48px] shrink-0 cursor-pointer rounded-full outline-offset-4 focus-visible:outline-2"
          onClick={(e) => {
            e.stopPropagation()
            toggle()
          }}
        >
          <AudioProgressRing size={48} progress={status.progress} />
          <div className="absolute inset-0 flex items-center justify-center">
            {status.isPlaying ? (
              <SvgPause aria-hidden className="h-[16px] w-[16px]" />
            ) : (
              <SvgPlay aria-hidden className="ml-[4px] h-[16px] w-[16px]" />
            )}
          </div>
        </button>
      </div>
      <audio ref={audioRef} {...audioProps} />
    </div>
  )
}
